import { PostBlockWeb, PostCollectionListDataSource } from '@nucleus/types/web';
import { WebHostingRouterInput } from '@nucleus/web-hosting';
import invariant from 'invariant';
import { flatMap as _flatMap, pick as _pick } from 'lodash';
import React, { useContext } from 'react';
import { useCollectionPath } from '../hooks/useCollectionPath';
import { useCollections } from '../hooks/useCollections';

type CollectionListInput = WebHostingRouterInput['postHub']['collection']['list'];
type UseCollections = ReturnType<typeof useCollections>;

type Context = Pick<UseCollections, 'status' | 'hasNextPage' | 'fetchNextPage' | 'isFetchingNextPage'> & {
  collections: any[];
  collectionType?: 'postCollections' | 'tags' | 'authors';
  getCollectionPath: (collection: any) => string;
};

const Context = React.createContext<Context>(null!);
Context.displayName = 'CollectionListController';

type Props = { children?: React.ReactNode } & CollectionListControllerOptions;

export const CollectionListController = ({ children, ...props }: Props): JSX.Element => {
  return <Context.Provider value={useController(props)}>{children}</Context.Provider>;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useCollectionListController = () => {
  invariant(
    useContext(Context) !== null,
    'useCollectionListController() may be used only in the context of a <CollectionListController> object'
  );
  return useContext(Context);
};

export type CollectionListControllerOptions = {
  engineId: string;
  basePath: string;
  collection?: 'postCollections' | 'tags' | 'authors';
  params?: Omit<CollectionListInput, 'hubId' | 'collectionType'>;
};

const useController = ({ basePath, ...options }: CollectionListControllerOptions): Context => {
  const { data, status, hasNextPage, fetchNextPage, isFetchingNextPage } = useCollections(options.engineId, options.collection, {
    limit: 5,
    ...options.params,
  });

  const getCollectionPath = useCollectionPath(basePath);

  return {
    collections: _flatMap(data?.pages, 'collections'),
    collectionType: options.collection,
    status: status,
    hasNextPage: hasNextPage,
    fetchNextPage: fetchNextPage,
    isFetchingNextPage: isFetchingNextPage,
    getCollectionPath: getCollectionPath,
  };
};

const getQueryParams = (dataSource: PostBlockWeb['dataSource']) => {
  const safeProps = _pick(dataSource, 'limit', 'order', 'orderBy');

  const paramEntries = Object.entries(safeProps).map(([key, value]) => [key, value]);

  return Object.fromEntries(paramEntries);
};

export const usePostBlockDataSourceCollectionListParams = ({
  dataSource,
  items,
}: PostBlockWeb): Pick<CollectionListControllerOptions, 'collection' | 'params'> | undefined => {
  if (!isCollectionDataSource(dataSource)) {
    return;
  }

  const baseParams = getQueryParams(dataSource);

  const ItemTypeCollectionMap = {
    postCollection: 'postCollections',
    author: 'authors',
    topic: 'tags',
  } satisfies Record<string, 'postCollections' | 'tags' | 'authors'>;

  if (dataSource.type === 'static' && items && items.length > 0) {
    return {
      collection: ItemTypeCollectionMap[dataSource.itemType],
      params: {
        ...baseParams,
        ids: items.map((item) => item.sourceId).join(','),
      },
    };
  }

  return {
    collection: ItemTypeCollectionMap[dataSource.itemType],
    params: baseParams,
  };
};

export const isCollectionDataSource = (dataSource: any): dataSource is PostCollectionListDataSource => {
  return ['postCollection', 'author', 'topic'].includes(dataSource?.itemType);
};
